import { useEffect, useState, useCallback } from 'react';
import { getBookingLimit, getPracticeBookingLimit } from 'api/marketing';
import { IBookingLimitVo, IPracticeBookingLimitVo } from 'types/bookingLimit';

/**
 * 当前 provider 自己的新患者周上限与用量。
 *
 * 上限本身是 practice 级的一份数据 —— 这里只是「我的视角」。从 Practice View 保存后，
 * 这份数据即失效，需要调用返回的 refetch。
 */
export const useBookingLimit = (): [IBookingLimitVo | undefined, boolean, () => void] => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IBookingLimitVo>();

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const result = await getBookingLimit();
            if (!result.error && result?.data?.data) {
                setData(result.data.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return [data, loading, fetchData];
};

/**
 * Practice 级总览：规则 + 汇总 + 每个成员的用量与状态。
 *
 * @param enabled 非 owner/admin 调用会拿到 4003，所以由调用方按权限决定是否发请求
 */
export const usePracticeBookingLimit = (
    enabled = true,
): [IPracticeBookingLimitVo | undefined, boolean, () => void] => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<IPracticeBookingLimitVo>();

    const fetchData = useCallback(async () => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const result = await getPracticeBookingLimit();
            if (!result.error && result?.data?.data) {
                setData(result.data.data);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return [data, loading, fetchData];
};
